/**
 * TanStack Query hooks for a Resource Plan's Actuals tab (planned vs actual + margin).
 *
 * Freshness (workspace spec AC6): actuals live in the app's copy of NOS time entries, so
 * the read first re-pulls this Solution's entries from NOS when the last pull is older
 * than 15 minutes, then reads. The Refresh button forces a pull regardless.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { send } from "@/hooks/use-resource-plans";
import { resourcePlanKeys } from "@/lib/query";
import type { PlanActualsRefreshView, PlanActualsView } from "@/schemas/plan-actuals";

const STALE_AFTER_MS = 15 * 60 * 1000;

/**
 * Plans whose automatic pull this session found NOS unconfigured, or that failed. Without
 * this, a server with no Gateway (or a Demand NOS keeps failing on) would re-try the pull
 * on every refetch of a stale plan. The Refresh button still forces one.
 */
const noAutoRefresh = new Set<string>();

function isStale(view: PlanActualsView, now: number): boolean {
  if (view.linkedDemandCount === 0) return false;
  if (view.asOf === null) return true;
  return now - new Date(view.asOf).getTime() > STALE_AFTER_MS;
}

async function refresh(planId: string): Promise<PlanActualsRefreshView> {
  const result = await send<PlanActualsRefreshView>(`/api/plans/${planId}/actuals/refresh`, "POST");
  if (result.skipped || result.errors > 0 || result.partial) noAutoRefresh.add(planId);
  return result;
}

export function usePlanActuals(planId: string, enabled: boolean) {
  return useQuery({
    queryKey: resourcePlanKeys.actuals(planId),
    queryFn: async () => {
      const url = `/api/plans/${planId}/actuals`;
      const first = await send<PlanActualsView>(url, "GET");
      if (!isStale(first, Date.now()) || noAutoRefresh.has(planId)) return first;
      try {
        await refresh(planId);
      } catch {
        noAutoRefresh.add(planId);
        // A failed pull still leaves the last synced figures worth showing; the
        // "as of" stamp tells the reader how old they are.
        return first;
      }
      return send<PlanActualsView>(url, "GET");
    },
    enabled,
  });
}

export function useRefreshPlanActuals(planId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => refresh(planId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: resourcePlanKeys.actuals(planId) });
      if (result.throttled) {
        toast.success("Actuals were pulled from NOS in the last two minutes — already current.");
      } else if (result.skipped) {
        toast.error("NOS isn't connected on this server, so actuals can't be refreshed.");
      } else if (result.partial || result.errors > 0) {
        toast.warning("Actuals refreshed, but NOS didn't return every page. Try again shortly.");
      } else {
        toast.success("Actuals refreshed from NOS.");
      }
    },
    onError: (err: Error) => toast.error(err.message),
  });
}
